export type OAuthErrorCode =
  | "cancelled"
  | "existing_account_link_required"
  | "identity_already_linked"
  | "email_collision"
  | "callback_failed"
  | "profile_setup_failed";

const RECOVERY_CODES = new Set<OAuthErrorCode>([
  "cancelled",
  "existing_account_link_required",
  "identity_already_linked",
  "email_collision",
  "callback_failed",
  "profile_setup_failed",
]);

/**
 * Convert provider/Supabase failures into a small privacy-safe vocabulary.
 * Raw descriptions can contain callback details and must never be copied into
 * the UI, URL, analytics, or logs.
 */
export function classifyOAuthError(
  oauthError: string | null | undefined,
  oauthErrorDescription?: string | null,
): OAuthErrorCode {
  const code = String(oauthError ?? "").toLowerCase();
  const description = String(oauthErrorDescription ?? "").toLowerCase();
  const combined = `${code} ${description}`;

  if (RECOVERY_CODES.has(code as OAuthErrorCode)) return code as OAuthErrorCode;
  if (code === "access_denied" || combined.includes("cancel")) return "cancelled";
  if (
    combined.includes("identity_already_exists") ||
    combined.includes("identity already") ||
    combined.includes("already linked") ||
    combined.includes("belongs to another")
  ) return "identity_already_linked";
  if (
    combined.includes("email_exists") ||
    combined.includes("email already") ||
    combined.includes("account already") ||
    combined.includes("existing account")
  ) return "email_collision";
  if (
    combined.includes("pkce") ||
    combined.includes("code verifier") ||
    combined.includes("exchange") ||
    combined.includes("callback") ||
    combined.includes("external code")
  ) return "callback_failed";
  return "callback_failed";
}

export function friendlyOAuthError(
  oauthError: string | null | undefined,
  oauthErrorDescription?: string | null,
): string {
  switch (classifyOAuthError(oauthError, oauthErrorDescription)) {
    case "cancelled":
      return "Sign in was cancelled.";
    case "existing_account_link_required":
    case "identity_already_linked":
    case "email_collision":
      return "This sign-in option could not be connected safely. Continue with email, then connect it from Login & security.";
    case "profile_setup_failed":
      return "You signed in, but we couldn't load your account. Please try again.";
    default:
      return "We couldn't complete social sign-in. Please try again, or continue with email.";
  }
}
